import { motion, useReducedMotion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Layers,
} from "lucide-react";
import { useState } from "react";

import { usePanel } from "@/contexts/PanelContext";

import { useAppContext } from "../../contexts/AppContext";
import { useTemplateContext } from "../../contexts/TemplateContext";
import {
  PALETTE_LAYOUT_SPRING,
  useCompactPalette,
} from "../../hooks/useCompactPalette";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CanvasCore } from "../../lib/canvas";
import { useSidebarWidth } from "../Header/useSidebarWidth";
import { SIDEBAR_SPRING } from "../SidebarBase";

export const OverlaySwitcher = () => {
  const {
    blankOverlay,
    setBlankOverlay,
    heatmapOverlay,
    setHeatmapOverlay,
    gridOverlay,
    setGridOverlay,
    pixelPulses,
    setPixelPulses,
    settings,
  } = useAppContext();
  const Panel = usePanel();
  const template = useTemplateContext();
  const reduceMotion = useReducedMotion() ?? false;
  const sidebarWidth = useSidebarWidth("Right");
  const compactPalette = useCompactPalette();
  const isMobile = useIsMobile();
  const offsetForSidebar = Boolean(
    !isMobile &&
      (Panel.state.settings || Panel.state.whois) &&
      settings.get["ui.adaptElementsToSidebar"],
  );

  const activeCount = [
    blankOverlay.enabled,
    heatmapOverlay.enabled,
    gridOverlay.enabled,
    pixelPulses,
    template.enable,
  ].filter(Boolean).length;
  const [collapsed, setCollapsed] = useState(() => activeCount === 0);

  if (isMobile || !settings.get["ui.showOverlaysCard"]) return null;

  const handleTemplateToggle = () => {
    const enabled = !template.enable;
    template.setEnable(enabled);

    if (enabled && template.x === undefined && template.y === undefined) {
      const canvas = CanvasCore.get(false);
      if (canvas) {
        const { canvasX, canvasY } = canvas.panZoomTransformToCanvas();
        template.setX(canvasX);
        template.setY(canvasY);
      }
    }

    if (!enabled || template.url?.trim()) return;

    Panel.dispatch(["settings", true]);
    window.requestAnimationFrame(() => {
      const section = document.getElementById("template-settings");
      section?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
      section
        ?.querySelector<HTMLInputElement>('input[aria-label="Template URL"]')
        ?.focus({ preventScroll: true });
    });
  };

  return (
    <motion.div
      animate={{
        x: offsetForSidebar ? -sidebarWidth : 0,
        bottom: compactPalette ? 84 : 76,
      }}
      id="overlay-switcher"
      className={`toolbar-box canvas-overlay-card${collapsed ? " overlay-switcher--collapsed" : ""}`}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { x: SIDEBAR_SPRING, bottom: PALETTE_LAYOUT_SPRING }
      }
    >
      <button
        type="button"
        className="overlay-switcher-collapse"
        aria-expanded={!collapsed}
        aria-label={
          collapsed ? "Expand map overlays" : "Collapse map overlays"
        }
        title={collapsed ? "Expand map overlays" : "Collapse map overlays"}
        onClick={() => setCollapsed((value) => !value)}
      >
        {collapsed ? (
          <ChevronLeft aria-hidden="true" />
        ) : (
          <ChevronRight aria-hidden="true" />
        )}
      </button>

      <div
        className={`overlay-switcher-content${activeCount > 0 ? " has-active-count" : ""}`}
      >
        <div aria-hidden={!collapsed} className="overlay-switcher-summary">
            <Layers aria-hidden="true" />
            <span>Overlays</span>
            {activeCount > 0 && (
              <span className="overlay-switcher-count">{activeCount}</span>
            )}
        </div>
        <div
          aria-hidden={collapsed}
          aria-label="Map overlays"
          className="overlay-switcher-items"
          role="group"
        >
            <button
              type="button"
              className={`overlay-switcher-item overlay-switcher-item--blank${blankOverlay.enabled ? " is-active" : ""}`}
              aria-pressed={blankOverlay.enabled}
              tabIndex={collapsed ? -1 : undefined}
              onClick={() =>
                setBlankOverlay((value) => ({
                  ...value,
                  enabled: !value.enabled,
                }))
              }
            >
              <span className="overlay-switcher-visual" aria-hidden="true" />
              <span>Blank Canvas</span>
            </button>

            <button
              type="button"
              className={`overlay-switcher-item overlay-switcher-item--heatmap${heatmapOverlay.enabled ? " is-active" : ""}`}
              aria-pressed={heatmapOverlay.enabled}
              aria-busy={heatmapOverlay.loading}
              tabIndex={collapsed ? -1 : undefined}
              onClick={() =>
                setHeatmapOverlay((value) => ({
                  ...value,
                  enabled: !value.enabled,
                }))
              }
            >
              <span className="overlay-switcher-visual" aria-hidden="true" />
              <span>Heatmap</span>
            </button>

            <button
              type="button"
              className={`overlay-switcher-item overlay-switcher-item--grid${gridOverlay.enabled ? " is-active" : ""}`}
              aria-pressed={gridOverlay.enabled}
              tabIndex={collapsed ? -1 : undefined}
              onClick={() =>
                setGridOverlay((value) => ({
                  ...value,
                  enabled: !value.enabled,
                }))
              }
            >
              <span className="overlay-switcher-visual" aria-hidden="true" />
              <span>Grid</span>
            </button>

            <button
              type="button"
              className={`overlay-switcher-item overlay-switcher-item--pulses${pixelPulses ? " is-active" : ""}`}
              aria-pressed={pixelPulses}
              tabIndex={collapsed ? -1 : undefined}
              onClick={() => setPixelPulses(!pixelPulses)}
            >
              <span className="overlay-switcher-visual" aria-hidden="true" />
              <span>Pulses</span>
            </button>

            <button
              type="button"
              className={`overlay-switcher-item overlay-switcher-item--template${template.enable ? " is-active" : ""}`}
              aria-pressed={template.enable}
              tabIndex={collapsed ? -1 : undefined}
              onClick={handleTemplateToggle}
            >
              <span className="overlay-switcher-visual" aria-hidden="true">
                {template.url ? (
                  <span
                    className="overlay-switcher-template-image"
                    style={{
                      backgroundImage: `url(${JSON.stringify(template.url)})`,
                    }}
                  />
                ) : (
                  <ImageIcon />
                )}
              </span>
              <span>Template</span>
            </button>
        </div>
      </div>
    </motion.div>
  );
};
